import { Loader2 } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from 'react-router-dom';

export default function PdfRouteViewer() {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadAttempts, setLoadAttempts] = useState(0);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const { t } = useTranslation();

    const [searchParams] = useSearchParams();
    const url = searchParams.get('url') || '';

    useEffect(() => {
        const loadPdf = () => {
            try {
                const parsedUrl = JSON.parse(url);
                if (parsedUrl) {
                    const encodedUrl = encodeURIComponent(parsedUrl);
                    const timestamp = Date.now();
                    const googleViewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true&chrome=false&dov=1&t=${timestamp}`;
                    setPdfUrl(googleViewerUrl);
                }
            } catch (err) {
                console.error("Error loading PDF:", err);
            }
        };

        loadPdf();
    }, [loadAttempts, url]);

    useEffect(() => {
        if (pdfUrl && isLoading) {
            timeoutRef.current = setTimeout(() => {
                if (loadAttempts < 3) {
                    setLoadAttempts(prev => prev + 1);
                    setIsLoading(true);
                } else {
                    setIsLoading(false);
                }
            }, 2000);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [pdfUrl, isLoading, loadAttempts]);

    const handleIframeLoad = () => {
        setIsLoading(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleIframeError = () => {
        if (loadAttempts < 3) {
            setTimeout(() => {
                setLoadAttempts(prev => prev + 1);
                setIsLoading(true);
            }, 1000);
        } else {
            setIsLoading(false);
        }
    };

    const forceRefresh = () => {
        setLoadAttempts(0);
        setIsLoading(true);
        if (iframeRef.current) {
            const currentSrc = iframeRef.current.src;
            iframeRef.current.src = '';
            setTimeout(() => {
                if (iframeRef.current) {
                    const newTimestamp = Date.now();
                    const urlWithNewTimestamp = currentSrc.replace(/&t=\d+/, `&t=${newTimestamp}`);
                    iframeRef.current.src = urlWithNewTimestamp;
                }
            }, 100);
        }
    };

    if (!pdfUrl) {
        return (
            <div className="w-screen h-screen flex items-center justify-center">
                <p></p>
            </div>
        );
    }

    return (
        <div className="w-screen h-screen relative">
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                    <div className="text-center">
                        {loadAttempts < 3 && <div className="flex items-start justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>}
                        {loadAttempts >= 3 && (
                            <button
                                onClick={forceRefresh}
                                className="mt-4 px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded text-sm"
                            >
                                {t('RETRY_LOADING')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <iframe
                ref={iframeRef}
                key={`pdf-${loadAttempts}-${pdfUrl}`}
                src={pdfUrl}
                title="PDF Preview"
                className="w-full h-full border-none"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                allow="autoplay"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                loading="eager"
                style={{
                    opacity: isLoading ? 0.3 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                }}
            />
        </div>
    );
}